import { useCallback, useEffect, useRef, useState } from 'react'
import type { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'

export type MediaItem = {
  type: 'image' | 'video'
  src: string
  /** Small stand-in used for the grid tile, so tiles cost a fraction of the original */
  thumb?: string
  /** Intrinsic width / height, known before the file loads */
  aspect?: number
  /** i18n key for the caption shown over the media in the modal */
  description?: string
  /** i18n key for the source credit, appended to the description */
  credit?: string
  /** Optional link on the credit, e.g. the channel a clip came from */
  creditHref?: string
  /** Capture date, read from a `YYYY.MM.DD` prefix on the file name */
  date?: string
}

/** Tailwind's `sm` breakpoint — below it the grid becomes a single-item carousel */
const DESKTOP_QUERY = '(min-width: 640px)'
const DESKTOP_PER_PAGE = 3

/**
 * On desktop tiles always take a third of the row, so a partial page stays
 * centred: a six column grid lets one item start at column 3, two at column 2
 * and three at column 1, each spanning two columns. Mobile shows one per page,
 * full width, so it needs none of this.
 */
const COL_START = ['', 'sm:col-start-3', 'sm:col-start-2', 'sm:col-start-1']

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches)

  useEffect(() => {
    const query = window.matchMedia(DESKTOP_QUERY)
    const sync = () => setIsDesktop(query.matches)
    sync()
    query.addEventListener('change', sync)
    return () => query.removeEventListener('change', sync)
  }, [])

  return isDesktop
}

interface Props {
  /** Any list of photos and clips — the carousel owns no media of its own */
  items: MediaItem[]
  className?: string
}

export default function MediaCarousel({ items, className }: Props) {
  const { t } = useTranslation()
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const [offset, setOffset] = useState(0)
  const perPage = useIsDesktop() ? DESKTOP_PER_PAGE : 1

  const close = useCallback(() => setOpenIndex(null), [])
  const navigate = useCallback(
    (step: number) => setOpenIndex((i) => (i === null ? i : (i + step + items.length) % items.length)),
    [items.length]
  )

  if (items.length === 0) return null

  // A carousel with nowhere to go needs no arrows
  const paged = items.length > perPage
  const windowSize = Math.min(perPage, items.length)

  // The window advances one item at a time and wraps, so the arrows shift the
  // row along rather than flipping to a whole new set
  const indices = Array.from({ length: windowSize }, (_, i) => (offset + i) % items.length)
  const go = (step: number) => setOffset((o) => (o + step + items.length) % items.length)

  return (
    <div className={`w-full ${className ?? ''}`}>
      {/* Arrows are flex siblings rather than overlays, so the media sits
          between them instead of underneath them */}
      <div className='flex items-center gap-1 sm:gap-2'>
        {paged && <Arrow direction='prev' label={t('media.previous')} onClick={() => go(-1)} />}

        <div className='grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-6 sm:gap-3'>
          {indices.map((mediaIndex, index) => {
            const item = items[mediaIndex]
            return (
              <button
                key={item.src}
                type='button'
                onClick={() => setOpenIndex(mediaIndex)}
                aria-label={item.description ? t(item.description) : t('media.view')}
                className={[
                  index === 0 ? COL_START[windowSize] : '',
                  'relative aspect-square cursor-pointer overflow-hidden rounded-sm sm:col-span-2',
                  'border border-gray-300 bg-gray-100 shadow-sm transition hover:opacity-90',
                  'focus:outline-none focus:ring-2 focus:ring-blue-700',
                ].join(' ')}
              >
                {item.thumb || item.type === 'image' ? (
                  <img
                    src={item.thumb ?? item.src}
                    alt={item.description ? t(item.description) : ''}
                    loading='lazy'
                    decoding='async'
                    className='h-full w-full object-cover'
                  />
                ) : (
                  // No thumbnail to stand in, so fall back to the clip's own first
                  // frame — metadata only, never the whole file
                  <video
                    src={`${item.src}#t=0.1`}
                    muted
                    playsInline
                    preload='metadata'
                    tabIndex={-1}
                    aria-hidden='true'
                    className='h-full w-full object-cover'
                  />
                )}

                {item.type === 'video' && (
                  <span className='pointer-events-none absolute inset-0 flex items-center justify-center'>
                    <span className='flex h-11 w-11 items-center justify-center rounded-full bg-black/60'>
                      <svg
                        viewBox='0 0 24 24'
                        aria-hidden='true'
                        className='ml-0.5 h-5 w-5 fill-white'
                      >
                        <path d='M8 5v14l11-7z' />
                      </svg>
                    </span>
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {paged && <Arrow direction='next' label={t('media.next')} onClick={() => go(1)} />}
      </div>

      {openIndex !== null && (
        <MediaModal item={items[openIndex]} count={items.length} onClose={close} onNavigate={navigate} />
      )}
    </div>
  )
}

function Arrow({
  direction,
  label,
  onClick,
  onDark = false,
}: {
  direction: 'prev' | 'next'
  label: string
  onClick: (event: MouseEvent) => void
  /** Over the modal's dark backdrop the chevrons flip to white */
  onDark?: boolean
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      aria-label={label}
      className={[
        'flex h-11 w-8 shrink-0 cursor-pointer items-center justify-center self-center sm:w-11',
        'focus:outline-none focus:ring-2',
        onDark ? 'focus:ring-white' : 'focus:ring-blue-700',
      ].join(' ')}
    >
      <svg
        viewBox='0 0 24 24'
        aria-hidden='true'
        fill='none'
        strokeWidth='2.5'
        strokeLinecap='round'
        strokeLinejoin='round'
        // In the modal nothing sits behind the chevrons but the photo, so there
        // they carry their own shadow to stay visible over a pale frame
        style={onDark ? { filter: 'drop-shadow(0 1px 3px rgb(0 0 0 / 0.9))' } : undefined}
        className={`h-7 w-7 ${onDark ? 'stroke-white' : 'stroke-blue-700'}`}
      >
        <path d={direction === 'prev' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'} />
      </svg>
    </button>
  )
}

function MediaModal({
  item,
  count,
  onClose,
  onNavigate,
}: {
  item: MediaItem
  count: number
  onClose: () => void
  onNavigate: (step: number) => void
}) {
  const { t } = useTranslation()
  const closeRef = useRef<HTMLButtonElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  // Tagged with the src it was measured from, so stepping to the next item
  // drops the previous ratio during render rather than in an effect. Only a
  // fallback: anything with a known aspect is sized before it loads.
  const [measured, setMeasured] = useState<{ src: string; ratio: number } | null>(null)
  const aspect = item.aspect ?? (measured?.src === item.src ? measured.ratio : null)
  const canNavigate = count > 1
  const isDesktop = useIsDesktop()

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') return onClose()
      // The native video controls seek with the arrow keys, so while one has
      // focus they keep them
      if ((event.target as HTMLElement | null)?.tagName === 'VIDEO') return
      if (event.key === 'ArrowLeft') onNavigate(-1)
      if (event.key === 'ArrowRight') onNavigate(1)
    }
    document.addEventListener('keydown', onKeyDown)

    const previousOverflow = document.body.style.overflow
    const previouslyFocused = document.activeElement as HTMLElement | null
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      previouslyFocused?.focus?.()
    }
  }, [onClose, onNavigate])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    // Opening the modal is a user gesture, so sound is normally allowed. Where
    // a browser refuses it anyway, start muted rather than not at all — the
    // controls carry an unmute toggle.
    video.muted = false
    video.play().catch(() => {
      video.muted = true
      video.play().catch(() => {})
    })
  }, [item.src])

  const stopClose = (event: { stopPropagation: () => void }) => event.stopPropagation()

  const media = (className: string) =>
    item.type === 'video' ? (
      <video
        ref={videoRef}
        src={item.src}
        poster={item.thumb}
        controls
        playsInline
        onLoadedMetadata={(event) => {
          const el = event.currentTarget
          if (el.videoHeight) setMeasured({ src: item.src, ratio: el.videoWidth / el.videoHeight })
        }}
        className={className}
      />
    ) : (
      <img
        src={item.src}
        alt={item.description ? t(item.description) : ''}
        onLoad={(event) => {
          const el = event.currentTarget
          if (el.naturalHeight) {
            setMeasured({ src: item.src, ratio: el.naturalWidth / el.naturalHeight })
          }
        }}
        className={className}
      />
    )

  const caption = item.description && (
    <>
      {t(item.description)}
      {(item.credit || item.date) && ' — '}
      {item.credit &&
        (item.creditHref ? (
          <a
            href={item.creditHref}
            target='_blank'
            rel='noopener noreferrer'
            className='underline underline-offset-2'
          >
            {t(item.credit)}
          </a>
        ) : (
          <span>{t(item.credit)}</span>
        ))}
      {item.credit && item.date && ', '}
      {item.date}
    </>
  )

  const prevArrow = (
    <Arrow
      direction='prev'
      label={t('media.previous')}
      onDark
      onClick={(event) => {
        event.stopPropagation()
        onNavigate(-1)
      }}
    />
  )

  const nextArrow = (
    <Arrow
      direction='next'
      label={t('media.next')}
      onDark
      onClick={(event) => {
        event.stopPropagation()
        onNavigate(1)
      }}
    />
  )

  return (
    <div
      role='dialog'
      aria-modal='true'
      aria-label={item.description ? t(item.description) : t('media.view')}
      onClick={onClose}
      // Sized by `inset-0` rather than `vh`, which overshoots the visible area
      // on mobile while the browser chrome is showing. Insets clear the notch.
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
      className='fixed inset-0 z-50 flex flex-col overflow-hidden bg-gray-900/90'
    >
      <div className='flex w-full shrink-0 justify-end p-2 sm:p-3'>
        <button
          ref={closeRef}
          type='button'
          onClick={onClose}
          aria-label={t('media.close')}
          className={[
            'flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center',
            'rounded-full bg-white text-blue-700 shadow-sm hover:bg-blue-50 transition',
            'focus:outline-none focus:ring-2 focus:ring-white',
          ].join(' ')}
        >
          <svg
            viewBox='0 0 24 24'
            aria-hidden='true'
            fill='none'
            strokeWidth='2'
            strokeLinecap='round'
            className='h-5 w-5 stroke-current'
          >
            <path d='M5 5l14 14M19 5L5 19' />
          </svg>
        </button>
      </div>

      {isDesktop ? (
        // min-h-0 / min-w-0 let the media shrink to the leftover space rather
        // than forcing the row to overflow
        <div className='flex min-h-0 min-w-0 flex-1 items-stretch gap-2 px-3 pb-3'>
          {canNavigate && prevArrow}

          {/* The track takes what the arrows leave, so media + nav can never
              exceed the viewport. Given the media's aspect ratio the figure
              hugs it exactly, keeping the caption on the media's edge */}
          <div className='flex min-h-0 min-w-0 flex-1 items-center justify-center'>
            <figure
              onClick={stopClose}
              style={aspect ? { aspectRatio: String(aspect) } : undefined}
              className='relative flex max-h-full max-w-full items-center justify-center'
            >
              {media('max-h-full max-w-full rounded-sm object-contain')}

              {aspect && caption && (
                <figcaption
                  // The bar is barely there, so the shadow — not the background
                  // — is what keeps the text legible over a bright or busy frame
                  style={{ textShadow: '0 1px 3px rgb(0 0 0 / 0.9), 0 0 8px rgb(0 0 0 / 0.6)' }}
                  className={[
                    // Video keeps its bottom edge clear for the native controls
                    item.type === 'video' ? 'top-0 rounded-t-sm' : 'bottom-0 rounded-b-sm',
                    'absolute inset-x-0 bg-white/10 backdrop-blur-sm',
                    'px-3 py-2 text-left text-lg leading-snug text-white',
                  ].join(' ')}
                >
                  {caption}
                </figcaption>
              )}
            </figure>
          </div>

          {canNavigate && nextArrow}
        </div>
      ) : (
        // Stacked so the media owns the full width — nothing competes with it
        // horizontally, and the caption and arrows sit underneath
        <>
          <div className='flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center gap-3 overflow-hidden px-2'>
            {/* Nothing stretches: the media, its caption and the arrows are their
                own height and stack tight, then the column centres the lot */}
            <figure
              onClick={stopClose}
              className='flex w-full min-w-0 max-w-[calc(100vw-1rem)] shrink-0 flex-col gap-2'
            >
              {/* Capped in viewport units rather than percentages: a percentage
                  chain only holds if every ancestor resolves a definite size, and
                  one that does not lets the media render at its intrinsic width.
                  `w-full` plus the ratio fixes the box before the file loads. */}
              <div
                style={aspect ? { aspectRatio: String(aspect) } : undefined}
                className='max-h-[55dvh] w-full max-w-[calc(100vw-1rem)] shrink-0'
              >
                {media('h-full w-full rounded-sm object-contain')}
              </div>

              {caption && (
                <figcaption
                  // Capped like the media above it, and allowed to break mid-word so
                  // no long token can push the box past the screen
                  className={[
                    'w-full min-w-0 max-w-[calc(100vw-1rem)] shrink-0 break-words',
                    'text-left text-base leading-snug text-white',
                  ].join(' ')}
                >
                  {caption}
                </figcaption>
              )}
            </figure>
          </div>

          {/* A sibling of the media track rather than a child, so it is pinned to
              the bottom of the modal and never moves as the media height varies */}
          {canNavigate && (
            <div className='flex shrink-0 items-center justify-center gap-10 px-2 pt-2 pb-3'>
              {prevArrow}
              {nextArrow}
            </div>
          )}
        </>
      )}
    </div>
  )
}
